import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import type { ChatDirectoryEntry } from '@bits/shared/zod';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Avatar } from '@/components/ui/avatar';

export function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

export function useDirectory(query: string, enabled = true) {
  const q = useDebounced(query.trim(), 200);
  return useQuery({
    queryKey: ['chat', 'directory', q],
    queryFn: () => api.chatDirectory(q || undefined),
    enabled,
    staleTime: 15_000,
  });
}

export function PeoplePicker({
  excludeIds,
  selected,
  onChange,
  autoFocus = false,
}: {
  excludeIds: readonly string[];
  selected: readonly ChatDirectoryEntry[];
  onChange: (next: ChatDirectoryEntry[]) => void;
  autoFocus?: boolean;
}): React.ReactElement {
  const [query, setQuery] = React.useState('');
  const directory = useDirectory(query);
  const excluded = React.useMemo(
    () => new Set([...excludeIds, ...selected.map((p) => p.id)]),
    [excludeIds, selected],
  );
  const candidates = (directory.data ?? []).filter((p) => !excluded.has(p.id));

  const add = (person: ChatDirectoryEntry): void => {
    onChange([...selected, person]);
    setQuery('');
  };
  const remove = (id: string): void => onChange(selected.filter((p) => p.id !== id));

  return (
    <div className="flex flex-col gap-3">
      {selected.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {selected.map((p) => (
            <li
              key={p.id}
              className="flex items-center gap-1.5 rounded-full bg-[hsl(var(--accent))] py-1 pl-1 pr-2 text-xs font-medium text-[hsl(var(--accent-foreground))]"
            >
              <Avatar name={p.name} personId={p.id} size="sm" />
              {p.name}
              <button
                type="button"
                onClick={() => remove(p.id)}
                aria-label={`Remove ${p.name}`}
                className="rounded-full p-0.5 hover:bg-[hsl(var(--brand))]/10"
              >
                <X className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
        <Input
          autoFocus={autoFocus}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search people by name, email or ID"
          className="pl-10"
          aria-label="Search people"
        />
      </div>
      <ul
        className="max-h-56 overflow-y-auto rounded-xl border border-[hsl(var(--border))]"
        aria-label="People"
      >
        {directory.isPending ? (
          <li className="px-3 py-3 text-sm text-[hsl(var(--muted-foreground))]">Loading…</li>
        ) : candidates.length === 0 ? (
          <li className="px-3 py-3 text-sm text-[hsl(var(--muted-foreground))]">
            {query ? 'No one matches.' : 'Everyone is already included.'}
          </li>
        ) : (
          candidates.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => add(p)}
                className={cn(
                  'flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-[hsl(var(--muted))]',
                )}
              >
                <Avatar name={p.name} personId={p.id} size="sm" online={p.online} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{p.name}</span>
                  <span className="block truncate text-xs text-[hsl(var(--muted-foreground))]">
                    {p.employeeId ? `${p.employeeId} · ` : ''}
                    {p.email}
                  </span>
                </span>
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}
